import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", address: "", court_name: "" };

export default function AdminVenues() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/venues").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/venues/${editing.id}`, form);
      else await api.post("/venues", form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (v) => {
    if (!window.confirm(`Delete venue "${v.name}"?`)) return;
    await api.delete(`/venues/${v.id}`); toast.success("Deleted"); load();
  };

  return (
    <div className="p-6 md:p-10">
      <PageHeader
        title="Venues"
        subtitle="Arenas and outdoor courts"
        testid="page-venues"
        action={
          <Button data-testid="create-venue-btn" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm">
            <Plus size={16} className="mr-2" /> New venue
          </Button>
        }
      />

      <div className="border border-navy-border rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-navy-surface text-[10px] font-heading uppercase tracking-widest text-ink-muted">
          <div className="col-span-4">Name</div>
          <div className="col-span-3">Court</div>
          <div className="col-span-4">Address</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {items.length === 0 ? <div className="p-8 text-center text-ink-muted">No venues</div> :
          items.map((v) => (
            <div key={v.id} className="grid grid-cols-12 items-center px-4 py-3 border-t border-navy-border" data-testid={`venue-row-${v.id}`}>
              <div className="col-span-4 font-heading uppercase tracking-tight text-white">{v.name}</div>
              <div className="col-span-3 text-ink-secondary text-sm">{v.court_name || "—"}</div>
              <div className="col-span-4 text-ink-secondary text-sm">{v.address || "—"}</div>
              <div className="col-span-1 flex justify-end gap-1">
                <Button data-testid={`edit-venue-${v.id}`} size="icon" variant="ghost" onClick={() => { setEditing(v); setForm({ ...EMPTY, ...v }); setOpen(true); }} className="text-ink-secondary hover:text-orange hover:bg-navy-surface"><Pencil size={14} /></Button>
                <Button data-testid={`delete-venue-${v.id}`} size="icon" variant="ghost" onClick={() => remove(v)} className="text-ink-secondary hover:text-court-red hover:bg-navy-surface"><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-navy-deep border-navy-border text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight text-2xl">{editing ? "Edit venue" : "New venue"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="venue-form">
            <div>
              <Label className="text-ink-secondary uppercase tracking-widest text-xs">Name</Label>
              <Input data-testid="venue-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-navy border-navy-border mt-2 rounded-sm" />
            </div>
            <div>
              <Label className="text-ink-secondary uppercase tracking-widest text-xs">Court</Label>
              <Input value={form.court_name || ""} onChange={(e) => setForm({ ...form, court_name: e.target.value })} className="bg-navy border-navy-border mt-2 rounded-sm" />
            </div>
            <div>
              <Label className="text-ink-secondary uppercase tracking-widest text-xs">Address</Label>
              <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-navy border-navy-border mt-2 rounded-sm" />
            </div>
            <DialogFooter>
              <Button data-testid="venue-submit-btn" type="submit" className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm">{editing ? "Save" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
